import moment from 'moment';
import AverageReportCollection from 'common/collections/average-report-collection';
import FacilityCollection from 'common/collections/facility-collection';
import IntensityReportCollection from 'common/collections/intensity-report-collection';
import PointsCollection from 'common/collections/points-collection';
import TimeseriesReportCollection from 'common/collections/timeseries-report-collection';
import AnalyticAppDisplayRule from 'common/constants/analytic-app-display-rule';
import {
  AnalyticAppErrorMessageEnum,
  ReportingPeriodEnum,
} from 'common/proxies/enum-proxy';
import {COMPLETE, SELECTION_CHANGED} from 'common/constants';
import {fetchCollections} from 'common/utils/backbone-util';
import {getEnumBySlug} from 'common/utils/enum-util';
import AvatarOrbView from 'trends-block/views/avatar-orb-view';
import ChartView from 'trends-block/views/chart-view';
import ComparisonSelectView from 'trends-block/views/comparison-select-view';
import DashboardLinkView from 'trends-block/views/dashboard-link-view';
import OdometerLabelView from 'trends-block/views/odometer-label-view';
import OdometerView from 'trends-block/views/odometer-view';
import PeriodSelectView from 'trends-block/views/period-select-view';
import TimestampView from 'trends-block/views/timestamp-view';
import NavView from 'trends-block/views/nav-view';
import SelectorLabelView from 'trends-block/views/selector-label-view';

const points = window.objectsJSON.points;
const building = window.objectsJSON.building;

const app = {};
app.delay = 300000;
app.settings = window.appSettings;
app.systemName = app.settings.systemName;
app.timeout = undefined;

// Collections
app.averageReportCollection = new AverageReportCollection();
app.comparisonCollection = new TimeseriesReportCollection();
app.facilityCollection = new FacilityCollection(building);
app.intensityReportCollection = new IntensityReportCollection();
app.pointsCollection = new PointsCollection(points);
app.timeseriesReportCollection = new TimeseriesReportCollection();

// Views

app.avatarOrbView = new AvatarOrbView({
  collections: {
    facilityCollection: app.facilityCollection,
    intensityReportCollection: app.intensityReportCollection,
  },
  el: $('#avatarOrb'),
  settings: app.settings,
});

app.timestampView = new TimestampView({
  el: $('#timestamp'),
});

app.dashboardLinkView = new DashboardLinkView({
  el: $('#dashboardLink'),
  settings: app.settings,
});

app.navView = new NavView({
  el: $('#nav'),
});

app.odometerLabelView = new OdometerLabelView({
  collection: app.pointsCollection,
  el: $('#odometerLabel'),
  systemName: app.systemName,
});

app.odometerView = new OdometerView({
  collection: app.averageReportCollection,
  el: $('#odometer'),
  systemName: app.systemName,
});

app.selectorLabelView = new SelectorLabelView({
  collection: app.pointsCollection,
  el: $('#selectorLabel'),
  elementID: '#selectorLabel',
  systemName: app.systemName,
});

app.periodSelectView = new PeriodSelectView({
  el: $('#periodSelect'),
  settings: app.settings,
});

app.comparisonSelectView = new ComparisonSelectView({
  el: $('#comparisonSelect'),
  settings: app.settings,
});

app.chartView = new ChartView({
  el: $('#chart'),
  settings: app.settings,
  systemName: app.systemName,
});

// Methods

app.fetchReportData = function () {
  const collection = app.pointsCollection;
  const point = collection.getPoint(app.systemName);
  if (!point) {
    app.showErrorMessage(AnalyticAppErrorMessageEnum.NO_METER.message);
    return;
  }
  const period = app.getPeriod();
  const comparison = app.getComparison();
  const resolution = app.getResolution(period, point);
  const params = app.getParams(period, resolution, comparison);
  const collections = app.getCollections(comparison);
  app.showLoadingView();
  fetchCollections(params, collections).on(COMPLETE, () => {
    app.renderReportData(params, collections, period);
  });
  app.setTimeout();
};

app.getCollections = function (comparison) {
  const collections = [
    app.averageReportCollection,
    app.timeseriesReportCollection,
    app.intensityReportCollection,
  ];
  if (comparison.slug !== ReportingPeriodEnum.NONE.slug) {
    collections.push(app.comparisonCollection);
  }
  return collections;
};

app.getComparison = function () {
  return app.comparisonSelectView.getSelectedItem();
};

app.getIntegrate = function (period) {
  switch (period.slug) {
    case ReportingPeriodEnum.MONTH.slug:
      return true;
    case ReportingPeriodEnum.YEAR.slug:
      return true;
    default:
      return false;
  }
};

app.getParams = function (period, resolution, comparison) {
  const systemName = app.systemName;
  const integrate = app.getIntegrate(period);
  const params = [
    {
      period: period.slug,
      predictions: 1,
      systemNames: [systemName],
    },
    {
      period: period.slug,
      resolution: resolution.slug,
      meter: systemName,
      integrate,
    },
    {
      meter: systemName,
    },
  ];
  if (comparison.slug !== ReportingPeriodEnum.NONE.slug) {
    params.push({
      page: 2,
      period: period.slug,
      resolution: resolution.slug,
      meter: systemName,
      integrate,
    });
  }
  return params;
};

app.getPeriod = function () {
  return app.periodSelectView.getSelectedItem();
};

app.getResolution = function (period, point) {
  const rules = app.getRules(period, point);
  const periodRules = rules.periods[period.slug];
  return periodRules.resolution;
};

app.getRules = function (period, point) {
  return getEnumBySlug(point.type.slug, AnalyticAppDisplayRule);
};

app.init = function () {
  this.initMoment();
  app.renderFacilityData();
  app.fetchReportData();
  app.setTimeout();
};

app.initMoment = function () {
  if (moment) {
    // Start weeks on Mondays
    moment.updateLocale('en', {week: {dow: 1}});
  }
};

app.renderFacilityData = function () {
  app.avatarOrbView.render();
  app.navView.render();
  app.selectorLabelView.render();
  const mainPeriod = app.periodSelectView.getSelectedItem();
  app.comparisonSelectView.setMainPeriod(mainPeriod);
};

app.renderReportData = function (params, collections, period) {
  const collection = this.pointsCollection;
  const meter = params[1].meter;
  const point = collection.getPoint(meter);
  app.avatarOrbView.render(point.systemName);
  app.odometerView.render();
  app.odometerLabelView.render(period);
  app.timestampView.render(collections);
  app.chartView.render(collections, period, point);
};

app.setTimeout = function () {
  clearTimeout(app.timeout);
  app.timeout = setTimeout(app.onTimeout, app.delay);
};

app.showErrorMessage = function (message) {
  app.chartView.showMessageState(message);
};

app.showLoadingView = function () {
  app.chartView.showLoadingState();
};

// Event Handlers

app.onComparisonChanged = function () {
  app.fetchReportData();
};

app.onPeriodChanged = function (event) {
  const period = event.selection;
  app.comparisonSelectView.setMainPeriod(period);
  app.fetchReportData();
};

app.onTimeout = function () {
  app.fetchReportData();
};

// Event Listeners

app.window = window;
app.window.onresize = function () {
  app.chartView.resize();
};
app.periodSelectView.on(SELECTION_CHANGED, app.onPeriodChanged);
app.comparisonSelectView.on(SELECTION_CHANGED, app.onComparisonChanged);

export default app;
